import { Button, Group, Text } from "@mantine/core";
import { IconCheck, IconEye, IconX } from "@tabler/icons-react";
import InterestViewProfileIconButton from "@/pages/interests/components/InterestViewProfileIconButton";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import { useDisclosure } from "@mantine/hooks";
import AcceptInterestModal from "@/pages/interests/components/AcceptInterestModal";
import PaymentWindowExtendModal from "@/components/shared/PaymentWindowExtendModal";
import { usePaymentWindowUiDemo } from "@/shared/utils/paymentWindowUiDemo";
import InterestIconDecisionButtons from "@/pages/interests/components/InterestIconDecisionButtons";

export interface InterestRequestActionsProps {
  matchId: string;
  companyId: string;
  collaborationStatus: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
  requesterSignedNDA: boolean;
  receiverSignedNDA: boolean;
  paymentDeadline?: Date | string | null;
  onAccept?: (matchId: string) => void;
  onDecline?: (matchId: string) => void;
  onMakePayment?: (matchId: string) => void;
  onViewProfile?: (companyId: string, matchId?: string) => void;
  onOpenWorkspace?: (matchId: string) => void;
  isLoading?: boolean;
  /** Table rows use icon buttons; cards use horizontal groups. */
  variant?: "card" | "table";
}

export default function InterestRequestActions({
  matchId,
  companyId,
  collaborationStatus,
  isRequester,
  requesterPaid,
  receiverPaid,
  requesterSignedNDA,
  receiverSignedNDA,
  paymentDeadline,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
  isLoading = false,
  variant = "card",
}: InterestRequestActionsProps) {
  // These props are part of the API contract; the table variant currently doesn't render NDA-specific UI.
  void requesterSignedNDA;
  void receiverSignedNDA;

  const [acceptModalOpened, { open: openAcceptModal, close: closeAcceptModal }] = useDisclosure(false);

  const userPaid = isRequester ? requesterPaid : receiverPaid;

  const showDeadline =
    !!paymentDeadline &&
    (collaborationStatus === CollaborationStatus.BOTH_ACCEPTED ||
      collaborationStatus === CollaborationStatus.REQUESTER_PAID_ONLY ||
      collaborationStatus === CollaborationStatus.RECEIVER_PAID_ONLY);

  const { modalOpen, handleExtend, handleModalDismiss } = usePaymentWindowUiDemo(
    matchId,
    paymentDeadline,
    showDeadline,
  );

  const canRespondToRequest =
    collaborationStatus === CollaborationStatus.REQUESTED && !isRequester;

  const renderCta = () => {
    if (canRespondToRequest) {
      if (variant === "table") {
        return (
          <InterestIconDecisionButtons
            acceptLabel="Accept request"
            acceptTitle="Accept collaboration request?"
            acceptMessage="Do you want to accept this collaboration request?"
            rejectLabel="Reject request"
            rejectTitle="Reject collaboration request?"
            rejectMessage="Do you want to reject this collaboration request?"
            onAccept={openAcceptModal}
            onReject={() => onDecline?.(matchId)}
            isLoading={isLoading}
          />
        );
      }
      return (
        <>
          <Button
            variant="filled"
            color="primary"
            size="sm"
            radius="lg"
            leftSection={<IconCheck size={16} />}
            onClick={openAcceptModal}
            loading={isLoading}
          >
            Accept
          </Button>
          <Button
            variant="outline"
            color="gray"
            size="sm"
            radius="lg"
            leftSection={<IconX size={16} />}
            onClick={() => onDecline?.(matchId)}
            loading={isLoading}
          >
            Decline
          </Button>
        </>
      );
    }

    if (
      (collaborationStatus === CollaborationStatus.BOTH_ACCEPTED ||
        collaborationStatus === CollaborationStatus.REQUESTER_PAID_ONLY ||
        collaborationStatus === CollaborationStatus.RECEIVER_PAID_ONLY) &&
      !userPaid
    ) {
      if (variant === "table") return null;
      return (
        <Button
          variant="filled"
          color="primary"
          size="sm"
          radius="lg"
          onClick={() => onMakePayment?.(matchId)}
          loading={isLoading}
        >
          Make Payment
        </Button>
      );
    }

    if (userPaid && collaborationStatus === CollaborationStatus.BOTH_PAID) {
      if (variant === "table") return null;
      return (
        <Button
          variant="filled"
          color="green"
          size="sm"
          radius="lg"
          onClick={() => onOpenWorkspace?.(matchId)}
          loading={isLoading}
        >
          Open Workspace
        </Button>
      );
    }

    if (userPaid) {
      return null;
    }

    if (collaborationStatus === CollaborationStatus.PAYMENT_EXPIRED) {
      if (variant === "table") return null;
      return (
        <Text size="sm" c="orange.6" fw={500}>
          Payment Expired
        </Text>
      );
    }

    return null;
  };

  const inlineCta = renderCta();

  const isDeclined = collaborationStatus === CollaborationStatus.RECEIVER_DECLINED;
  const openMatchWorkspace = () => {
    if (onOpenWorkspace) {
      onOpenWorkspace(matchId);
      return;
    }
    onViewProfile?.(companyId, matchId);
  };

  const viewProfileControl =
    variant === "table" ? (
      <InterestViewProfileIconButton
        onClick={() => (isDeclined ? openMatchWorkspace() : onViewProfile?.(companyId))}
      />
    ) : (
      <Button
        variant="outline"
        size="sm"
        radius="lg"
        color="primary"
        leftSection={<IconEye size={16} />}
        onClick={() => (isDeclined ? openMatchWorkspace() : onViewProfile?.(companyId))}
        style={{ flexShrink: 0 }}
        styles={{
          root: {
            borderColor: "var(--mantine-color-primary-5)",
            color: "var(--mantine-color-primary-7)",
          },
        }}
      >
        View
      </Button>
    );

  return (
    <>
      {variant === "table" ? (
        <Group gap={6} wrap="nowrap" justify="flex-start" align="center">
          {viewProfileControl}
          {inlineCta}
        </Group>
      ) : (
        <Group
          gap="sm"
          wrap="wrap"
          w="100%"
          styles={{
            root: {
              justifyContent: "flex-start",
              "@media (min-width: 48em)": { justifyContent: "flex-end" },
            },
          }}
        >
          {inlineCta}
          {viewProfileControl}
        </Group>
      )}

      <AcceptInterestModal
        opened={acceptModalOpened}
        onClose={closeAcceptModal}
        onConfirm={() => {
          closeAcceptModal();
          onAccept?.(matchId);
        }}
        isLoading={isLoading}
      />

      <PaymentWindowExtendModal opened={modalOpen} onExtend={handleExtend} onDismiss={handleModalDismiss} />
    </>
  );
}
